"use client"

import { useState } from "react"
import { ArrowLeft, Clock, CheckCircle, AlertCircle, User, Calendar, MessageSquare, Edit, Save, X } from "lucide-react"

export default function EngagementDetails({ userType, engagementId, onBack }) {
  const [isEditing, setIsEditing] = useState(false)
  const [newComment, setNewComment] = useState("")
  const [statusUpdate, setStatusUpdate] = useState("")

  // Mock engagement data
  const engagement = {
    id: 1,
    trackingId: "FB-2024-001",
    title: "Road Maintenance Issue",
    description:
      "Pothole on Main Street causing traffic issues and potential vehicle damage. The hole is approximately 2 feet wide and 6 inches deep.",
    category: "Road Maintenance",
    status: "in-progress",
    priority: "high",
    citizen: "John Doe",
    citizenEmail: "john.doe@email.com",
    citizenPhone: "+1 (555) 123-4567",
    assignedTo: "Road Department",
    assignedUser: "Mike Johnson",
    assignedUserEmail: "mike.j@agency.gov",
    submittedAt: "2024-01-15T10:30:00Z",
    updatedAt: "2024-01-18T14:20:00Z",
    estimatedResolution: "2024-01-25",
    location: "Main Street, between 1st and 2nd Avenue",
    timeline: [
      {
        id: 1,
        type: "submitted",
        title: "Feedback Submitted",
        description: "Citizen reported road maintenance issue",
        timestamp: "2024-01-15T10:30:00Z",
        user: "John Doe",
        userType: "citizen",
      },
      {
        id: 2,
        type: "assigned",
        title: "Assigned to Department",
        description: "Feedback assigned to Road Department",
        timestamp: "2024-01-16T09:15:00Z",
        user: "Admin",
        userType: "agency",
      },
      {
        id: 3,
        type: "status_update",
        title: "Status Updated",
        description: "Investigation started - team dispatched to assess the damage",
        timestamp: "2024-01-18T14:20:00Z",
        user: "Mike Johnson",
        userType: "agency",
      },
    ],
    attachments: [
      { id: 1, name: "pothole_photo.jpg", size: "2.3 MB", type: "image", url: "/placeholder.svg?height=200&width=300" },
      { id: 2, name: "location_map.pdf", size: "1.1 MB", type: "document", url: "#" },
    ],
    comments: [
      {
        id: 1,
        user: "Mike Johnson",
        userType: "agency",
        message: "We've received your report and will investigate within 24 hours.",
        timestamp: "2024-01-16T10:00:00Z",
      },
      {
        id: 2,
        user: "John Doe",
        userType: "citizen",
        message: "Thank you for the quick response. The pothole seems to be getting worse with recent rain.",
        timestamp: "2024-01-17T08:30:00Z",
      },
    ],
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="w-5 h-5" />
      case "in-progress":
        return <AlertCircle className="w-5 h-5" />
      case "resolved":
        return <CheckCircle className="w-5 h-5" />
      default:
        return <Clock className="w-5 h-5" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800"
      case "in-progress":
        return "bg-blue-100 text-blue-800"
      case "resolved":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800"
      case "medium":
        return "bg-yellow-100 text-yellow-800"
      case "low":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const handleAddComment = () => {
    if (newComment.trim()) {
      // In real app, this would make an API call
      console.log("Adding comment:", newComment)
      setNewComment("")
    }
  }

  const handleStatusUpdate = () => {
    if (statusUpdate.trim()) {
      // In real app, this would make an API call
      console.log("Updating status:", statusUpdate)
      setStatusUpdate("")
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{engagement.title}</h1>
          <p className="text-gray-600">Tracking ID: {engagement.trackingId}</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(engagement.status)}`}
          >
            {getStatusIcon(engagement.status)}
            <span className="ml-1 capitalize">{engagement.status.replace("-", " ")}</span>
          </span>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(engagement.priority)}`}
          >
            {engagement.priority}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Details */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Details</h2>
              {userType === "agency" && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="flex items-center gap-2 text-primary hover:text-primary/80"
                >
                  {isEditing ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                  {isEditing ? "Cancel" : "Edit"}
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    defaultValue={engagement.title}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={4}
                    defaultValue={engagement.description}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div className="flex justify-end">
                  <button className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Description</p>
                  <p className="text-gray-900">{engagement.description}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Location</p>
                  <p className="text-gray-900">{engagement.location}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Category</p>
                  <p className="text-gray-900">{engagement.category}</p>
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          {engagement.attachments && engagement.attachments.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {engagement.attachments.map((attachment) => (
                  <div key={attachment.id} className="border border-gray-200 rounded-lg p-4">
                    {attachment.type === "image" ? (
                      <img
                        src={attachment.url || "/placeholder.svg"}
                        alt={attachment.name}
                        className="w-full h-32 object-cover rounded-lg mb-2"
                      />
                    ) : (
                      <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                        <span className="text-4xl">📄</span>
                      </div>
                    )}
                    <p className="text-sm font-medium text-gray-900 truncate">{attachment.name}</p>
                    <p className="text-xs text-gray-500">{attachment.size}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Timeline</h2>
            <div className="space-y-6">
              {engagement.timeline.map((event, index) => (
                <div key={event.id} className="flex">
                  <div className="flex flex-col items-center mr-4">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                    </div>
                    {index < engagement.timeline.length - 1 && <div className="w-0.5 h-6 bg-gray-300 mt-2"></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">{event.title}</h4>
                      <p className="text-xs text-gray-500">{formatDate(event.timestamp)}</p>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      By: {event.user} ({event.userType})
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Comments</h2>
            <div className="space-y-4 mb-6">
              {engagement.comments.map((comment) => (
                <div key={comment.id} className="flex space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900">{comment.user}</p>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          comment.userType === "agency" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"
                        }`}
                      >
                        {comment.userType}
                      </span>
                      <p className="text-xs text-gray-500">{formatDate(comment.timestamp)}</p>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">{comment.message}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Comment */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex space-x-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-white">{userType === "citizen" ? "C" : "A"}</span>
                </div>
                <div className="flex-1">
                  <textarea
                    rows={3}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Add a comment..."
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Comment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Citizen Information */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Citizen Information</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <User className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{engagement.citizen}</p>
                  <p className="text-xs text-gray-500">Citizen</p>
                </div>
              </div>
              <div className="flex items-center">
                <MessageSquare className="w-5 h-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-900">{engagement.citizenEmail}</p>
                  <p className="text-xs text-gray-500">Email</p>
                </div>
              </div>
              {engagement.citizenPhone && (
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-900">{engagement.citizenPhone}</p>
                    <p className="text-xs text-gray-500">Phone</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assignment Information */}
          {userType === "agency" && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Assignment</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Department</p>
                  <p className="text-sm text-gray-900">{engagement.assignedTo}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Assigned User</p>
                  <p className="text-sm text-gray-900">{engagement.assignedUser}</p>
                  <p className="text-xs text-gray-500">{engagement.assignedUserEmail}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Estimated Resolution</p>
                  <p className="text-sm text-gray-900">
                    {new Date(engagement.estimatedResolution).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status Update */}
          {userType === "agency" && engagement.status !== "resolved" && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Status</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Status</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Update Message</label>
                  <textarea
                    rows={3}
                    value={statusUpdate}
                    onChange={(e) => setStatusUpdate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Describe the status update..."
                  />
                </div>
                <button
                  onClick={handleStatusUpdate}
                  className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90"
                >
                  Update Status
                </button>
              </div>
            </div>
          )}

          {/* Key Dates */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Dates</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">Submitted</p>
                <p className="text-sm text-gray-900">{formatDate(engagement.submittedAt)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Last Updated</p>
                <p className="text-sm text-gray-900">{formatDate(engagement.updatedAt)}</p>
              </div>
              {engagement.estimatedResolution && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Estimated Resolution</p>
                  <p className="text-sm text-gray-900">
                    {new Date(engagement.estimatedResolution).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
